import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const RISKS = [
    { id: "RSK-001", name: "Data Breach in Production", likelihood: 2, impact: 5, color: "red" },
    { id: "RSK-002", name: "Non-compliance with GDPR", likelihood: 1, impact: 5, color: "orange" },
    { id: "RSK-003", name: "Third-party Vendor Failure", likelihood: 3, impact: 4, color: "red" },
    { id: "RSK-004", name: "Market Volatility", likelihood: 4, impact: 3, color: "orange" },
    { id: "RSK-005", name: "Internal Fraud Potential", likelihood: 2, impact: 4, color: "orange" },
    { id: "RSK-006", name: "Ransomware Attack", likelihood: 3, impact: 5, color: "red" },
    { id: "RSK-007", name: "Public Relations Crisis", likelihood: 2, impact: 3, color: "yellow" },
    { id: "RSK-008", name: "Regulatory Change Surprise", likelihood: 2, impact: 4, color: "orange" },
    { id: "RSK-009", name: "Business Continuity Failure", likelihood: 1, impact: 5, color: "orange" },
    { id: "RSK-010", name: "API Rate Limiting Issues", likelihood: 4, impact: 2, color: "yellow" },
];

export default function HeatMap() {
    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-medium text-foreground">Risk Heat Map</h1>
                <p className="text-muted-foreground">Interactive 5×5 matrix visualizing probability vs. severity.</p>
            </div>

            <Card className="border-none shadow-premium bg-gradient-to-br from-card to-card/50 overflow-hidden">
                <CardHeader>
                    <CardTitle>Likelihood vs. Impact Matrix</CardTitle>
                    <CardDescription>Risks in the top-right quadrant require immediate executive attention.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="relative flex flex-col md:flex-row gap-8 py-4">
                        {/* Impact Y-Axis Label */}
                        <div className="hidden md:flex flex-col justify-around items-center h-[500px] py-10">
                            <span className="text-xs font-bold text-muted-foreground vertical-text uppercase tracking-widest -rotate-180" style={{ writingMode: 'vertical-rl' }}>Impact (1-5)</span>
                        </div>

                        {/* Grid Container */}
                        <div className="flex-1">
                            <div className="grid grid-cols-5 grid-rows-5 h-[500px] w-full border border-muted/50 rounded-lg overflow-hidden glass-morphism relative">
                                {/* Background color zones */}
                                {[...Array(25)].map((_, i) => {
                                    const row = 4 - Math.floor(i / 5); // 0 to 4 (bottom to top)
                                    const col = i % 5; // 0 to 4 (left to right)

                                    let bgColor = "bg-emerald-500/5"; // Low (Green)
                                    if ((row + col) >= 4) bgColor = "bg-amber-500/10"; // Medium (Amber)
                                    if ((row + col) >= 6) bgColor = "bg-red-500/15"; // High (Red)

                                    return <div key={i} className={`border border-muted/5 ${bgColor}`} />;
                                })}

                                {/* Risk Plotting */}
                                <TooltipProvider>
                                    {RISKS.map((risk, i) => {
                                        // Position calculations (left/bottom based on 1-5 scale)
                                        const leftPos = ((risk.likelihood - 0.5) / 5) * 100;
                                        const bottomPos = ((risk.impact - 0.5) / 5) * 100;

                                        return (
                                            <Tooltip key={risk.id}>
                                                <TooltipTrigger asChild>
                                                    <motion.div
                                                        initial={{ scale: 0, opacity: 0 }}
                                                        animate={{ scale: 1, opacity: 1 }}
                                                        transition={{ delay: i * 0.1 }}
                                                        className="absolute z-10 cursor-pointer pointer-events-auto"
                                                        style={{
                                                            left: `${leftPos}%`,
                                                            bottom: `${bottomPos}%`,
                                                            transform: 'translate(-50%, 50%)'
                                                        }}
                                                    >
                                                        <div className={`w-10 h-10 rounded-full border-2 border-background shadow-xl flex items-center justify-center text-[10px] font-bold text-white transition-transform hover:scale-125 hover:z-20
                              ${risk.impact * risk.likelihood >= 12 ? 'bg-red-500' : risk.impact * risk.likelihood >= 8 ? 'bg-amber-500' : 'bg-emerald-500'}
                            `}>
                                                            {risk.id.split('-')[1]}
                                                        </div>
                                                    </motion.div>
                                                </TooltipTrigger>
                                                <TooltipContent className="p-3 max-w-[200px] glass-morphism border-primary/20">
                                                    <p className="font-bold text-sm mb-1">{risk.name}</p>
                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                        <span>Likelihood: {risk.likelihood}</span>
                                                        <span>Impact: {risk.impact}</span>
                                                    </div>
                                                    <div className="mt-2 text-[10px] font-mono opacity-70">{risk.id}</div>
                                                </TooltipContent>
                                            </Tooltip>
                                        );
                                    })}
                                </TooltipProvider>

                                {/* Axis Labels (Inside) */}
                                <div className="absolute left-0 bottom-0 w-full flex justify-around text-[10px] font-medium opacity-40 py-1 translate-y-full">
                                    <span>1 Rare</span>
                                    <span>2 Unlikely</span>
                                    <span>3 Possible</span>
                                    <span>4 Likely</span>
                                    <span>5 Certain</span>
                                </div>
                            </div>
                            <div className="mt-8 text-center">
                                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Likelihood (1-5)</span>
                            </div>
                        </div>

                        {/* Legend / Key */}
                        <div className="w-full md:w-64 space-y-4">
                            <h4 className="text-sm font-semibold mb-4 px-2">Risk Priority Legend</h4>
                            <div className="space-y-3">
                                {[
                                    { label: "Critical (High-Red)", points: "12-25", color: "bg-red-500" },
                                    { label: "Moderate (Amber)", points: "8-11", color: "bg-amber-500" },
                                    { label: "Acceptable (Low-Green)", points: "1-7", color: "bg-emerald-500" },
                                ].map((item) => (
                                    <div key={item.label} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                                        <div className={`w-4 h-4 rounded ${item.color}`} />
                                        <div className="flex flex-col">
                                            <span className="text-xs font-medium">{item.label}</span>
                                            <span className="text-[10px] text-muted-foreground">{item.points} Score Points</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 p-4 rounded-xl bg-primary/5 border border-primary/10">
                                <p className="text-[11px] leading-relaxed text-muted-foreground italic">
                                    "The heat map provides a consolidated view of risk exposure across all business units, normalized to the Enterprise Risk Management framework."
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
